// src/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Creates a neat modal layout using pure JavaScript elements and Tailwind CSS
const injectTailwindModal = (title, message, redirectUrl) => {
  if (document.getElementById('api-forced-modal')) return;

  const modalContainer = document.createElement('div');
  modalContainer.id = 'api-forced-modal';
  modalContainer.className = 'fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4';

  modalContainer.innerHTML = `
    <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300 opacity-0" id="api-modal-overlay"></div>

    <div class="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 translate-y-full sm:translate-y-4 sm:scale-95 opacity-0 z-10 border border-gray-100" id="api-modal-card">
      <div class="h-1.5 w-full bg-gradient-to-r from-red-500 to-amber-500"></div>
      
      <div class="p-6 space-y-4">
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900">${title}</h3>
        </div>

        <p class="text-sm text-gray-600 leading-relaxed">${message}</p>
        
        <div class="pt-2">
          <button id="api-modal-btn" class="w-full py-2.5 px-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold text-sm rounded-xl transition-all active:scale-[0.98]">
            Log In Again
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modalContainer);

  const overlay = document.getElementById('api-modal-overlay');
  const card = document.getElementById('api-modal-card');
  const btn = document.getElementById('api-modal-btn');

  setTimeout(() => {
    overlay.classList.remove('opacity-0');
    card.classList.remove('translate-y-full', 'sm:translate-y-4', 'sm:scale-95', 'opacity-0');
  }, 50);

  btn.addEventListener('click', () => {
    window.location.href = redirectUrl;
  });
};

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenantId');
    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!config.headers['x-tenant-id'] && !config.headers['X-Tenant-Id'] && tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthFlowRequest = /\/api\/new-auth\/(login|subdomain-signin|main-domain-signin|forgot-password|reset-password|change-password|check-sessions)/.test(requestUrl);

    if (error.response && error.response.status === 401) {
      const errorCode = error.response.data?.code;
      const keysToRemove = ['token', 'userId', 'userRole', 'tenantId', 'tenantName', 'tenantSubdomain', 'mustChangePassword', 'tokenVersion', 'sessionId'];
      
      if (errorCode === 'TOKEN_VERSION_MISMATCH') {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        injectTailwindModal(
          'Session Expired',
          'You have logged in on another device. Please sign in here again to continue.',
          '/?session_expired=logged_in_elsewhere'
        );
        return Promise.reject(error);
      }
      
      if (errorCode === 'LEGACY_TOKEN') {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        injectTailwindModal(
          'Security Update',
          'Please log in again to update your account with our latest system settings.',
          '/?reason=legacy_token'
        );
        return Promise.reject(error);
      }
      
      if (errorCode === 'SESSION_INACTIVE') {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        injectTailwindModal(
          'Session Terminated',
          'Your session has expired or was terminated. Please log in again to continue.',
          '/?reason=session_expired'
        );
        return Promise.reject(error);
      }
      
      if (!isAuthFlowRequest && !originalRequest._retry) {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// TENANT LEGAL DOCUMENT API FUNCTIONS
// ============================================

export const getTenantDocuments = async () => {
  try {
    const response = await api.get('/api/tenant-legal/documents');
    return response.data;
  } catch (error) {
    console.error('Error fetching tenant documents:', error);
    throw error;
  }
};

export const getTenantPendingCount = async () => {
  try {
    const response = await api.get('/api/tenant-legal/pending-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending count:', error);
    throw error;
  }
};

export const acceptTenantDocument = async (documentId) => {
  try {
    const response = await api.post('/api/tenant-legal/accept', { documentId });
    return response.data;
  } catch (error) {
    console.error('Error accepting document:', error);
    throw error;
  }
};

export const acceptAllTenantDocuments = async () => {
  try {
    const response = await api.post('/api/tenant-legal/accept-all');
    return response.data;
  } catch (error) {
    console.error('Error accepting all documents:', error);
    throw error;
  }
};

export default api;